import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Button, Stack, Typography } from '@mui/material'

import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'
import PageContainer from '@/components/ui/containers/PageContainer'

import { useDeleteApiKeyMutation, useGetApiKeyQuery, useUpdateApiKeyMutation } from '@/core/api'
import { useGetPoliciesQuery } from '@/core/services/policy'

import { getApiErrorMessage } from '@/utils/errors'

import ApiKeyForm from './components/ApiKeyForm'
import NotFoundPage from '@/pages/error/NotFoundPage'

export default function ApiKeyPage() {
  const navigate = useNavigate()
  const { apiKeyId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const { data: apiKey, isLoading: isFetchingApiKey } = useGetApiKeyQuery(apiKeyId ?? '', { skip: !apiKeyId })
  const { data: policies } = useGetPoliciesQuery()
  const [updateApiKey, { isLoading: isUpdatingApiKey }] = useUpdateApiKeyMutation()
  const [deleteApiKey, { isLoading: isDeletingApiKey }] = useDeleteApiKeyMutation()

  const [editable, setEditable] = useState(false)
  const [openDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] = useState(false)

  const policiesData = useMemo(
    () =>
      policies
        ?.filter((policy) => (editable ? true : apiKey?.policies.includes(policy._id)))
        .map((policy) => ({
          id: policy._id,
          name: policy.name,
          description: policy.description,
        })),
    [apiKey, editable, policies]
  )

  const initialValues = useMemo(
    () =>
      apiKey
        ? {
          name: apiKey.name,
          policies: policies?.filter((policy) => apiKey.policies.includes(policy._id)).map((policy) => policy._id) ?? [],
        }
        : undefined,
    [apiKey, policies]
  )

  if (isFetchingApiKey) {
    return null
  }

  if (!apiKey) return <NotFoundPage />

  return (
    <PageContainer>
      <Stack spacing={4}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h2">{apiKey.name}</Typography>
          <Stack direction="row" spacing={2}>
            <Button color="error" onClick={() => setOpenDeleteConfirmationDialog(true)} variant="contained">
              {t('common:button.delete.label')}
            </Button>
            <Button onClick={() => setEditable(!editable)} variant="contained">
              {!editable ? t('common:button.modify.label') : t('common:button.cancel.label')}
            </Button>
          </Stack>
        </Stack>
        <Stack spacing={3}>
          <ApiKeyForm
            key={editable ? 'editable' : 'readOnly'}
            initialValues={initialValues}
            isSubmitting={isUpdatingApiKey}
            onSubmit={async (values) => {
              try {
                await updateApiKey({ id: apiKey._id, ...values }).unwrap()
                setEditable(false)
              } catch (error) {
                enqueueSnackbar(getApiErrorMessage(error, t('notification.apiKey.update.error')), { variant: 'error' })
              }
            }}
            policies={policiesData}
            readOnly={!editable}
          />
        </Stack>
      </Stack>
      <DeleteConfirmationDialog
        isDeleting={isDeletingApiKey}
        maxWidth={800}
        onClose={() => {
          setOpenDeleteConfirmationDialog(false)
        }}
        onDelete={async () => {
          await deleteApiKey(apiKey._id).unwrap()
          setOpenDeleteConfirmationDialog(false)
          navigate('/admin/groups')
        }}
        open={openDeleteConfirmationDialog}
        title={t('dialog.deleteConfirmation.apiKey.title')}
        message={t('dialog.deleteConfirmation.apiKey.message')}
      />
    </PageContainer>
  )
}
